import type { RecordOfMatchesState } from '../matches/types';
import type { RecordOfUnionsState, SSRUnionsState } from './types';

import type { BackendEntities } from '../types/backendEntities';
import {
  fillStateWithMatchIds,
  removeMatches,
  transformNodeSSRState,
  updateField,
} from '../utils';
import { createUnionEntity, initialState } from './records';
import { transformUnion } from './transform';

// ---

// Transform the `entities.tournamentUnions` node's server-side rendered state.
export function transformUnionsSSRState(state: SSRUnionsState): RecordOfUnionsState {
  return transformNodeSSRState(state, initialState, (entry) => {
    return createUnionEntity({
      id: entry.id,
      name: entry.name,
      icon: entry.icon,
      sportId: entry.sportId,
      matchesCount: entry.matchesCount,
      priority: typeof entry.priority === 'number' ? entry.priority : 0,
      promotionLevel: typeof entry.promotionLevel === 'number' ? entry.promotionLevel : 0,
      matchIds: new Set(entry.matchIds),
    });
  });
}

// Tournament union entities reducer.
function tournamentUnionsReducer(
  state: RecordOfUnionsState = initialState(),
  clientEntities: BackendEntities | undefined,
  matchesState: RecordOfMatchesState['byId'] | undefined,
): RecordOfUnionsState {
  if (!clientEntities?.diff) {
    return state;
  }

  const {
    tournamentUnions,
    matches,
    tournamentUnionMatchesCount,
    removedMatches,
  } = clientEntities.diff;

  if (!(tournamentUnions || matches || tournamentUnionMatchesCount || removedMatches)) {
    return state;
  }

  let tournamentUnionsState = state.get('byId').asMutable();

  if (removedMatches && matchesState) {
    tournamentUnionsState = removeMatches(
      'tournamentUnionId',
      removedMatches,
      matchesState,
      tournamentUnionsState,
    );
  }

  if (tournamentUnions) {
    const tournamentUnionsKeys = Object.keys(tournamentUnions);

    tournamentUnionsKeys.forEach((tournamentUnionKey) => {
      const transformed = transformUnion(
        tournamentUnions[tournamentUnionKey],
      );

      if (tournamentUnionsState.has(transformed.id)) {
        if (transformed.name !== undefined) {
          tournamentUnionsState = updateField(tournamentUnionsState, transformed.id, 'name', transformed.name);
        }
        if (transformed.priority !== undefined) {
          tournamentUnionsState = updateField(tournamentUnionsState, transformed.id, 'priority', transformed.priority);
        }
        if (transformed.promotionLevel !== undefined) {
          tournamentUnionsState = updateField(tournamentUnionsState, transformed.id, 'promotionLevel', transformed.promotionLevel);
        }
        if (transformed.icon !== undefined) {
          tournamentUnionsState = updateField(tournamentUnionsState, transformed.id, 'icon', transformed.icon);
        }
      }
      else if (matches || tournamentUnionMatchesCount) {
        tournamentUnionsState = tournamentUnionsState.set(
          transformed.id,
          createUnionEntity(transformed),
        );
      }
    });
  }

  if (matches || tournamentUnionMatchesCount) {
    tournamentUnionsState = fillStateWithMatchIds(
      'tournamentUnionId',
      matches,
      tournamentUnionMatchesCount,
      tournamentUnionsState,
    );
  }

  return state.set('byId', tournamentUnionsState.asImmutable());
}

export default tournamentUnionsReducer;
